import { Button, Heading, Stack, Table, TableCaption, Tbody, Td, Tr } from '@chakra-ui/react'
import { faCheck, faTimes } from '@fortawesome/pro-regular-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import React, { type ReactElement } from 'react'

import chakraTokenToVar from '@/utils/chakraTokenToVar'

import SeriesHostQ from '@/queries/SeriesHostQ'
import SessionHostQ from '@/queries/SessionHostQ'
import UserQ from '@/queries/UserQ'
import type { Query } from '@/client/query'

import MessageBox from '@/components/UI/MessageBox'

import CohostReviewTableAvatar from './CohostReviewTableAvatar'
import type { HostToastInfo } from './types'

export default function CohostReviewTable({
  cohosts,
  numCohostRequests,
  isSession,
  onApproveCohost,
  isApprovingCohost,
  setRejectHostInfo,
  setShowModal,
}: {
  cohosts: Query[]
  numCohostRequests: number
  isSession: boolean
  onApproveCohost: (cohostInfo: HostToastInfo) => void
  isApprovingCohost: boolean
  setRejectHostInfo: (cohostInfo: HostToastInfo | null) => void
  setShowModal: (showModal: boolean) => void
}): ReactElement {
  return (
    <>
      <Heading size="xl">Co-host Requests ({numCohostRequests})</Heading>
      <Table variant="ghost">
        {numCohostRequests === 0 ? (
          <TableCaption>There are no current co-host requests to review.</TableCaption>
        ) : null}
        <Tbody>
          {cohosts.map(cohost => {
            const cohostId = (isSession ? cohost(SessionHostQ.id) : cohost(SeriesHostQ.id)) ?? 0
            const requestMessage = isSession
              ? `${cohost(SessionHostQ.request_message)}`
              : `${cohost(SeriesHostQ.request_message)}`
            const hostName = `${cohost(UserQ.display_name)}`
            return (
              <React.Fragment key={cohostId}>
                <Tr>
                  <Td pl="-4">
                    <Stack
                      direction="row"
                      width="100%"
                      spacing="8px"
                      justify="space-between"
                      align="center"
                    >
                      <CohostReviewTableAvatar user={cohost} />
                      <Stack direction="row">
                        <Button
                          variant="outline"
                          textColor="success.fg"
                          leftIcon={
                            <FontAwesomeIcon
                              color={chakraTokenToVar('success.fg')}
                              icon={faCheck}
                            />
                          }
                          borderColor="border.emphasized"
                          isLoading={isApprovingCohost}
                          onClick={() =>
                            onApproveCohost({
                              name: hostName,
                              hostId: cohostId,
                            })
                          }
                        >
                          Accept
                        </Button>
                        <Button
                          variant="outline"
                          textColor="error.fg"
                          borderColor="border.emphasized"
                          leftIcon={
                            <FontAwesomeIcon color={chakraTokenToVar('error.fg')} icon={faTimes} />
                          }
                          onClick={() => {
                            setRejectHostInfo({
                              name: hostName,
                              hostId: cohostId,
                            })
                            setShowModal(false)
                          }}
                        >
                          Reject
                        </Button>
                      </Stack>
                    </Stack>
                  </Td>
                </Tr>
                {requestMessage !== '' && requestMessage != null ? (
                  <Tr>
                    <MessageBox text={requestMessage} />
                  </Tr>
                ) : null}
              </React.Fragment>
            )
          })}
        </Tbody>
      </Table>
    </>
  )
}
